import type { ToolDefinition } from "@arcadeai/arcadejs/resources/index.mjs";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { OAUTH_PROVIDER_CATALOGUE } from "@arcadeai/design-system/metadata/oauth-providers";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import { TOOLKIT_CATALOGUE } from "@arcadeai/design-system/metadata/toolkits";
import { useNavigate } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";
import { DataTableFacetedFilter } from "@/components/custom/table/data-table-faceted-filter";
import { useToolsSearchParams } from "../hooks/use-tools-search-params";
import type { Tool } from "../types";

const SEARCH_DEBOUNCE_MS = 500;

type FilterOption = {
  label: string;
  value: string;
  icon?: React.ElementType;
};

type ToolsFiltersProps = {
  allTools: Tool[];
  searchValue: string;
  onSearchChange: (search: string) => void;
  viewOptions?: React.ReactNode;
};

export const ToolsFilters = ({
  allTools,
  searchValue,
  onSearchChange,
  viewOptions,
}: ToolsFiltersProps) => {
  const navigate = useNavigate();
  const { toolkits, authProviders, secrets } = useToolsSearchParams();

  // Compute filter options from tools
  const toolkitOptions = useMemo((): FilterOption[] => {
    const uniqueToolkits = new Set<string>();
    for (const tool of allTools) {
      const toolkitName = tool.toolkit?.name?.trim();
      if (toolkitName) {
        uniqueToolkits.add(toolkitName);
      }
    }
    return Array.from(uniqueToolkits).map((value) => {
      const toolkit = TOOLKIT_CATALOGUE[value];
      return {
        label: toolkit?.label || value,
        value,
        icon: getToolkitIcon(toolkit?.id ?? ""),
      };
    });
  }, [allTools]);

  const requirements = useMemo(
    () =>
      allTools
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        .map((tool) => tool.requirements as ToolDefinition.Requirements)
        .filter(Boolean),
    [allTools]
  );

  const authProviderOptions = useMemo((): FilterOption[] => {
    const uniqueProviders = new Set<string>();
    for (const req of requirements) {
      if (req.authorization?.provider_id) {
        uniqueProviders.add(req.authorization.provider_id);
      }
    }
    return Array.from(uniqueProviders).map((providerId) => {
      const provider = OAUTH_PROVIDER_CATALOGUE[providerId];
      return {
        label: provider?.name || providerId,
        value: providerId,
        icon: provider?.icon,
      };
    });
  }, [requirements]);

  const secretsOptions = useMemo((): FilterOption[] => {
    const uniqueSecrets = new Set<string>();
    for (const req of requirements) {
      if (req.secrets) {
        for (const secret of req.secrets) {
          uniqueSecrets.add(secret.key);
        }
      }
    }
    return Array.from(uniqueSecrets).map((key) => ({
      label: key,
      value: key,
    }));
  }, [requirements]);

  const updateUrlParams = useCallback(
    (updates: Record<string, unknown>) => {
      navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, ...updates }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate]
  );

  // Track the debounce timeout so we can cancel it on reset
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedUpdateSearch = useCallback(
    (value: string) => {
      // Clear any pending debounced update
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Schedule new update
      debounceTimeoutRef.current = setTimeout(() => {
        updateUrlParams({ search: value || undefined });
        debounceTimeoutRef.current = null;
      }, SEARCH_DEBOUNCE_MS);
    },
    [updateUrlParams]
  );

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    debouncedUpdateSearch(value);
  };

  const handleReset = () => {
    // Cancel any pending debounced search update
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    onSearchChange("");
    updateUrlParams({
      search: undefined,
      toolkits: undefined,
      authProviders: undefined,
      secrets: undefined,
    });
  };

  const isFiltered =
    !!searchValue ||
    toolkits.length > 0 ||
    authProviders.length > 0 ||
    secrets.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[200px] flex-1">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-8 pl-9 text-sm"
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search..."
          value={searchValue}
        />
      </div>

      {viewOptions}

      {authProviderOptions.length > 0 && (
        <DataTableFacetedFilter
          column={undefined}
          onSelectionChange={(values) =>
            updateUrlParams({
              authProviders: values.length > 0 ? values : undefined,
            })
          }
          options={authProviderOptions}
          selectedValues={authProviders}
          title="Auth Providers"
        />
      )}

      {secretsOptions.length > 0 && (
        <DataTableFacetedFilter
          column={undefined}
          onSelectionChange={(values) =>
            updateUrlParams({ secrets: values.length > 0 ? values : undefined })
          }
          options={secretsOptions}
          selectedValues={secrets}
          title="Secrets"
        />
      )}

      {toolkitOptions.length > 1 && (
        <DataTableFacetedFilter
          column={undefined}
          onSelectionChange={(values) =>
            updateUrlParams({
              toolkits: values.length > 0 ? values : undefined,
            })
          }
          options={toolkitOptions}
          selectedValues={toolkits}
          title="MCP Servers"
        />
      )}

      {isFiltered ? (
        <Button
          className="h-8 px-2 lg:px-3"
          onClick={handleReset}
          size="sm"
          variant="warning"
        >
          Reset
          <X className="ml-1 size-3.5" />
        </Button>
      ) : null}
    </div>
  );
};
